import React, { useState } from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { ProposeReinclusionModal } from "@/uiKit/organisms/ProposeReinclusionModal.tsx";

export type NurseReinclusionButtonViewProps = {
  state: "eligible" | "active";
  onPropose?: () => void;
  onCancel?: () => void;
  isCancelPending?: boolean;
  className?: string;
};

export const NurseReinclusionButtonView: React.FC<NurseReinclusionButtonViewProps> = ({
  state,
  onPropose,
  onCancel,
  isCancelPending,
  className,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  if (state === "active") {
    return (
      <div className={cx(styles.container, className)}>
        <Tag
          color="neutral"
          label={t("reinclusion.activeTag")}
          icon={<Icons.stethoscope className={styles.icon} />}
          className={styles.tag}
        />
        <Button
          id="cancel-reinclusion-request"
          variant="outlined"
          data-action="inclusion.reinclusion.cancel"
          pending={isCancelPending}
          onClick={onCancel}
        >
          {t("reinclusion.cancelAction")}
        </Button>
      </div>
    );
  }

  return (
    <div className={cx(styles.container, className)}>
      <Button
        id="open-propose-reinclusion-modal"
        variant="contained"
        data-action="inclusion.reinclusion.propose"
        startIcon={<Icons.stethoscope className={styles.icon} />}
        onClick={onPropose}
      >
        {t("reinclusion.actionButton")}
      </Button>
    </div>
  );
};

type NurseReinclusionButtonProps = {
  patientId: string;
  className?: string;
};

export const NurseReinclusionButton: React.FC<NurseReinclusionButtonProps> = ({ patientId, className }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const reinclusion = Queries.practitioner.useReinclusionStateForNurse(patientId);
  const requestReinclusion = Queries.practitioner.useRequestReinclusion();
  const cancelReinclusion = Queries.practitioner.useNurseCancelReinclusion();

  if (reinclusion.reinclusionRequestActive) {
    return (
      <NurseReinclusionButtonView
        state="active"
        onCancel={() => cancelReinclusion.mutate(patientId)}
        isCancelPending={cancelReinclusion.isPending}
        className={className}
      />
    );
  }

  if (!reinclusion.canReinclude) {
    return null;
  }

  return (
    <>
      <NurseReinclusionButtonView state="eligible" onPropose={() => setIsModalOpen(true)} className={className} />
      <ProposeReinclusionModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isPending={requestReinclusion.isPending}
        onSubmit={nurseMessage =>
          requestReinclusion.mutate({ patientId, nurseMessage }, { onSuccess: () => setIsModalOpen(false) })
        }
      />
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(4),
  }),
  tag: css`
    padding: ${theme.spacing(8)};
  `,
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
